import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TabList,
  Tab,
  SelectTabData,
  SelectTabEvent,
  Badge,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  Switch,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import WebhookEventDetailsPane from '../components/WebhookEventDetailsPane';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';

interface WebhookEvent {
  id: string;
  source: string;
  external_event_id: string;
  event_type: string;
  received_at: string | null;
  processed_at: string | null;
  error: string | null;
  status: 'success' | 'error' | 'pending';
}

interface ListResponse {
  data: WebhookEvent[];
  total: number;
  limit: number;
  offset: number;
}

type SourceTab = 'all' | 'booqable' | 'authnet';
type StatusFilter = 'all' | 'success' | 'error' | 'pending';

const POLL_INTERVAL_MS = 10_000;

const useStyles = makeStyles({
  page: {
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.padding('0', '16px'),
    flexWrap: 'wrap',
  },
  toolbarSpacer: {
    flex: 1,
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pollIndicator: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pollDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    boxShadow: tokens.shadow8,
    overflow: 'hidden',
  },
  tabListWrapper: {
    ...shorthands.padding('16px', '16px', '0', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  filterChips: {
    display: 'flex',
    gap: '8px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    alignItems: 'center',
  },
  filterLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginRight: '4px',
  },
  emptyState: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  errorMonospace: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '320px',
    display: 'inline-block',
  },
  eventTypeMono: {
    fontFamily: 'monospace',
    fontSize: '13px',
  },
  externalIdMono: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  },
});

const STATUS_INTENT: Record<WebhookEvent['status'], 'success' | 'danger' | 'warning'> = {
  success: 'success',
  error: 'danger',
  pending: 'warning',
};

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return formatBusinessDateTime(iso);
}

const Logs: React.FC = () => {
  const styles = useStyles();
  const [sourceTab, setSourceTab] = useState<SourceTab>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [eventModalId, setEventModalId] = useState<string | null>(null);
  const [pollingEnabled, setPollingEnabled] = useState(true);

  const queryParams = new URLSearchParams();
  queryParams.set('limit', '200');
  if (sourceTab !== 'all') queryParams.set('source', sourceTab);
  if (statusFilter !== 'all') queryParams.set('status', statusFilter);

  const { data, isLoading, error, refetch, isFetching } = useQuery<ListResponse>({
    queryKey: ['webhook-events', sourceTab, statusFilter],
    queryFn: async () => {
      const res = await apiFetch(`/webhooks/events?${queryParams.toString()}`);
      if (!res.ok) throw new Error('Failed to load webhook events');
      return res.json();
    },
    refetchInterval: pollingEnabled ? POLL_INTERVAL_MS : false,
    staleTime: 0,
  });

  const events = data?.data || [];

  const handleTabSelect = (_: SelectTabEvent, d: SelectTabData) => {
    setSourceTab(d.value as SourceTab);
  };

  const columns: ColumnConfig<WebhookEvent>[] = [
    {
      columnKey: 'received_at',
      label: 'Received',
      sortable: false,
      render: (e) => formatTimestamp(e.received_at),
    },
    {
      columnKey: 'source',
      label: 'Source',
      render: (e) => <Badge appearance="outline">{e.source}</Badge>,
    },
    {
      columnKey: 'event_type',
      label: 'Event',
      render: (e) => (
        <Button
          appearance="transparent"
          style={{ padding: 0, fontWeight: 500, minWidth: 0, height: 'auto' }}
          onClick={(ev) => { ev.stopPropagation(); setEventModalId(e.id); }}
        >
          <span className={styles.eventTypeMono}>{e.event_type}</span>
        </Button>
      ),
    },
    {
      columnKey: 'status',
      label: 'Status',
      render: (e) => (
        <Badge appearance="filled" color={STATUS_INTENT[e.status]}>
          {e.status}
        </Badge>
      ),
    },
    {
      columnKey: 'error',
      label: 'Error',
      render: (e) =>
        e.error ? <span className={styles.errorMonospace} title={e.error}>{e.error}</span> : '—',
    },
    {
      columnKey: 'external_event_id',
      label: 'Event ID',
      render: (e) => (
        <span className={styles.externalIdMono} title={e.external_event_id}>
          {e.external_event_id}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.toolbarRow}>
        <div className={styles.toolbarSpacer} />
        <div className={styles.toolbarRight}>
          <div className={styles.pollIndicator}>
            {pollingEnabled && <span className={styles.pollDot} />}
            <span>
              {pollingEnabled
                ? `Auto-refresh every ${POLL_INTERVAL_MS / 1000}s`
                : 'Auto-refresh paused'}
              {data && ` · ${data.total} total`}
            </span>
          </div>
          <Switch
            checked={pollingEnabled}
            onChange={(_, v) => setPollingEnabled(v.checked)}
            label="Auto-refresh"
          />
          <Button
            icon={<ArrowClockwise24Regular />}
            onClick={() => refetch()}
            disabled={isFetching}
            appearance="subtle"
            aria-label="Refresh now"
          >
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div style={{
          margin: '0 16px',
          padding: '12px',
          backgroundColor: '#fef0f0',
          border: '1px solid #fcd4d4',
          borderRadius: '4px',
          color: '#c42b1c',
        }}>
          {(error as Error).message}
        </div>
      )}

      <div className={styles.tableContainer}>
        <div className={styles.tabListWrapper}>
          <TabList selectedValue={sourceTab} onTabSelect={handleTabSelect}>
            <Tab value="all">All sources</Tab>
            <Tab value="booqable">Booqable</Tab>
            <Tab value="authnet">Auth.Net</Tab>
          </TabList>
        </div>

        <div className={styles.filterChips}>
          <span className={styles.filterLabel}>Status:</span>
          {(['all', 'success', 'error', 'pending'] as StatusFilter[]).map((s) => (
            <Button
              key={s}
              size="small"
              appearance={statusFilter === s ? 'primary' : 'subtle'}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>

        {isLoading && (
          <div className={styles.emptyState}>Loading webhook events...</div>
        )}

        {!isLoading && events.length === 0 && (
          <div className={styles.emptyState}>No webhook events match these filters.</div>
        )}

        {!isLoading && events.length > 0 && (
          <DataTable
            data={events}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="logs"
            itemsPerPage={50}
            showCheckboxes={false}
            primaryColumnKey="event_type"
            onRowClick={(e) => setEventModalId(e.id)}
          />
        )}
      </div>

      <Dialog
        open={!!eventModalId}
        onOpenChange={(_, { open }) => { if (!open) setEventModalId(null); }}
      >
        <DialogSurface style={{ maxWidth: '90vw', width: '900px', height: '85vh', padding: 0, overflow: 'hidden' }}>
          <DialogBody style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 8px 0' }}>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={() => setEventModalId(null)}
              />
            </div>
            <DialogContent style={{ flex: 1, overflow: 'auto', padding: 0 }}>
              {eventModalId && <WebhookEventDetailsPane eventId={eventModalId} />}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Logs;
